import React, { useEffect, useState, useContext } from 'react';
import ApiClient from '../../Services/ApiClient';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../store';
import { fetchMyMeetings } from '../../store/meetingsSlice';
import { selectMeetingsByPatient } from '../../store/selectors';
import { useAlert } from '../ui/alertProvider';
import { useResources } from '../../store/resourcesProvider';
import AuthContext from '../../store/authContext';
import { useNavigate } from 'react-router-dom';

const PatientMeetings: React.FC = () => {
    const [meetings, setMeetings] = useState<any[]>([]);
    const [payments, setPayments] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch<AppDispatch>();
    const auth = useContext(AuthContext) as any;
    const meetingsState = useSelector(selectMeetingsByPatient((auth && auth.userId) || null));
    const myMeetings = meetingsState.items;
    const alert = useAlert();
    const resources = useResources();
    const navigate = useNavigate();

    useEffect(() => { load(); }, []);

    // redirect if token missing/invalid
    useEffect(() => {
        const token = auth?.token ?? localStorage.getItem('token');
        try {
            const expired = auth && typeof auth.isTokenExpired === 'function'
                ? auth.isTokenExpired(token ?? '')
                : true;
            if (!token || expired) {
                navigate('/');
            }
        } catch {
            navigate('/');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth?.token]);

    const load = async () => {
        setLoading(true);
        try {
            if (auth && auth.userId) {
                const payload: any = await dispatch(fetchMyMeetings({ userId: auth.userId, page: 1, pageSize: 200 })).unwrap();
                setMeetings(payload?.result?.items ?? []);
            } else {
                const m: any = await ApiClient.get('/meetings/me');
                setMeetings(m?.items ?? []);
            }
        } catch (err) {
            console.error('Failed to load meetings', err);
            alert.showAlert(resources.get('PatientDashboard.LoadFailed','Failed to load meetings'), 'error');
        }
        try {
            const p: any = await ApiClient.get('/payments/me');
            setPayments(p ?? []);
        } catch (err) {
            console.warn('Failed to load payments', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-padding">
            <h2>{resources.get('PatientMeetings.Title','My Meetings & Payments')}</h2>
            {loading ? <div>{resources.get('PatientDashboard.Loading','Loading...')}</div> : (
                <div>
                    <section>
                        <h3>{resources.get('PatientDashboard.Meetings','Meetings')}</h3>
                        {meetings.length === 0 ? <div>{resources.get('PatientDashboard.NoMeetings','No meetings scheduled')}</div> : (
                            <ul className="meeting-item-list">
                                {meetings.map(m => (
                                    <li key={m.id} className="mt-6">
                                        <div className="meeting-item-title">{new Date(m.meetingDate).toLocaleString()}</div>
                                        <div>{m.summary}</div>
                                        <div className="mt-6">{m.isPaid ? <span className="text-green">{resources.get('PatientDashboard.Paid','Paid')}</span> : <span className="text-red">{resources.get('PatientDashboard.Unpaid','Unpaid')}</span>}</div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                    <section className="mt-12">
                        <h3>{resources.get('PatientDashboard.PaymentsTitle','Payments')}</h3>
                        {payments.length === 0 ? <div>{resources.get('PatientDashboard.NoPayments','No payments found')}</div> : (
                            <ul>
                                {payments.map(p => (
                                    <li key={p.paymentId} className="mb-6">
                                        <div>{p.amount} - {p.status}</div>
                                        <div className="small-text">{new Date(p.createdAt).toLocaleString()}</div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </div>
            )}
        </div>
    );
};

export default PatientMeetings;
